import * as THREE from "three";

const TAU = Math.PI * 2;

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function lerp(current, target, amount) {
  return current + (target - current) * amount;
}

function angleDelta(current, target) {
  let delta = (target - current) % TAU;
  if (delta > Math.PI) delta -= TAU;
  if (delta < -Math.PI) delta += TAU;
  return delta;
}

function seededRandom(index) {
  const value = Math.sin(index * 9187.127 + 31.4159) * 43758.5453;
  return value - Math.floor(value);
}

function disposeMaterial(material, disposedTextures) {
  if (!material) return;
  Object.keys(material).forEach(key => {
    const value = material[key];
    if (value && value.isTexture && !disposedTextures.has(value)) {
      disposedTextures.add(value);
      value.dispose();
    }
  });
  material.dispose();
}

export default class TerrainRaycastEngine {
  constructor(canvas, options = {}) {
    this.canvas = canvas;
    this.locations = options.locations || [];
    this.onSelect = options.onSelect || (() => undefined);
    this.onTerrainPick = options.onTerrainPick || (() => undefined);
    this.onCameraChange = options.onCameraChange || (() => undefined);

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x063f66);
    this.scene.fog = new THREE.FogExp2(0x063f66, 0.0042);

    this.camera = new THREE.PerspectiveCamera(64, 1, 0.1, 420);
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      alpha: false,
      powerPreference: "high-performance"
    });
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.08;

    this.raycaster = new THREE.Raycaster();
    this.pointerNdc = new THREE.Vector2();
    this.raycastTargets = [];
    this.locationGroups = new Map();
    this.pointerMap = new Map();

    this.elapsed = 0;
    this.running = false;
    this.frameHandle = 0;
    this.lastFrameTime = 0;
    this.raycastCount = 0;
    this.hoveredId = null;
    this.selectedId = this.locations[0] ? this.locations[0].id : null;
    this.terrainPick = null;

    this.cameraYaw = 0.84;
    this.cameraPitch = 1.08;
    this.cameraDistance = 98;
    this.desiredYaw = this.cameraYaw;
    this.desiredPitch = this.cameraPitch;
    this.desiredDistance = this.cameraDistance;
    this.minDistance = 40;
    this.maxDistance = 118;
    this.cameraTarget = new THREE.Vector3(0, 1.4, 0);
    this.desiredTarget = this.cameraTarget.clone();

    this.dragging = false;
    this.pinchStartDistance = 0;
    this.pinchBaseCameraDistance = this.cameraDistance;

    this.spinWheel = null;
    this.rouletteBall = null;
    this.portalCore = null;
    this.clouds = [];
    this.sunGroup = null;
    this.sunGlow = null;
    this.shoreRings = [];
    this.airMotes = null;
    this.tick = this.tick.bind(this);

    this.createWorld();
    this.updateCamera(1);
    this.updateSelectionVisuals();
    this.render();
  }

  heightAt(x, z) {
    const ellipse = Math.sqrt(
      (x * x) / (34.5 * 34.5) + (z * z) / (31.5 * 31.5)
    );
    const island = clamp(1 - ellipse, 0, 1);
    const ridge =
      Math.sin(x * 0.26) * 0.62 +
      Math.cos(z * 0.31) * 0.48 +
      Math.sin((x + z) * 0.17) * 0.38 +
      Math.cos(Math.sqrt(x * x + z * z) * 0.41) * 0.28;
    const terrace = Math.sin((x - z) * 0.11) * 0.34;
    return -3.3 + Math.pow(island, 1.36) * 10.2 + (ridge + terrace) * island;
  }

  createWorld() {
    this.createLights();
    this.createWater();
    this.createTerrain();
    this.createPaths();
    this.createDecorations();
    this.createLandmarks();
    this.createRaycastHelpers();
  }

  createLights() {
    const hemisphere = new THREE.HemisphereLight(0xd6f2ff, 0x17334a, 1.08);
    this.scene.add(hemisphere);

    const sun = new THREE.DirectionalLight(0xffdfa3, 1.66);
    sun.position.set(-24, 38, 18);
    this.scene.add(sun);

    const rim = new THREE.DirectionalLight(0x65e5ff, 0.76);
    rim.position.set(28, 16, -26);
    this.scene.add(rim);
  }

  createSky() {
    const sky = new THREE.Mesh(
      new THREE.SphereGeometry(190, 32, 20),
      new THREE.ShaderMaterial({
        uniforms: {
          uZenith: { value: new THREE.Color(0x10275a) },
          uMidSky: { value: new THREE.Color(0x2d7892) },
          uHorizon: { value: new THREE.Color(0x79c8cf) },
          uSunset: { value: new THREE.Color(0xf3b36d) }
        },
        vertexShader: `
          varying float vSkyHeight;
          varying vec3 vSkyPosition;

          void main() {
            vec3 direction = normalize(position);
            vSkyHeight = direction.y;
            vSkyPosition = direction;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          }
        `,
        fragmentShader: `
          uniform vec3 uZenith;
          uniform vec3 uMidSky;
          uniform vec3 uHorizon;
          uniform vec3 uSunset;
          varying float vSkyHeight;
          varying vec3 vSkyPosition;

          void main() {
            float height01 = clamp(vSkyHeight * 0.5 + 0.5, 0.0, 1.0);
            float upperMix = smoothstep(0.48, 0.9, height01);
            vec3 color = mix(uHorizon, uMidSky, smoothstep(0.22, 0.56, height01));
            color = mix(color, uZenith, upperMix);
            float sunsetBand = exp(-pow((height01 - 0.43) * 7.0, 2.0));
            float sunSide = smoothstep(-0.15, -0.85, vSkyPosition.x);
            color = mix(color, uSunset, sunsetBand * sunSide * 0.32);
            gl_FragColor = vec4(color, 1.0);
          }
        `,
        side: THREE.BackSide,
        depthWrite: false
      })
    );
    sky.renderOrder = -30;
    this.scene.add(sky);

    this.sunGroup = new THREE.Group();
    this.sunGroup.position.set(-30, 21, -45);
    this.sunGroup.userData.baseY = this.sunGroup.position.y;

    const glowCanvas = document.createElement("canvas");
    glowCanvas.width = 256;
    glowCanvas.height = 256;
    const glowContext = glowCanvas.getContext("2d");
    const glowGradient = glowContext.createRadialGradient(
      128,
      128,
      12,
      128,
      128,
      124
    );
    glowGradient.addColorStop(0, "rgba(255, 250, 208, 1)");
    glowGradient.addColorStop(0.18, "rgba(255, 218, 120, 0.92)");
    glowGradient.addColorStop(0.48, "rgba(255, 179, 78, 0.34)");
    glowGradient.addColorStop(1, "rgba(255, 159, 58, 0)");
    glowContext.fillStyle = glowGradient;
    glowContext.fillRect(0, 0, 256, 256);

    const glowTexture = new THREE.CanvasTexture(glowCanvas);
    glowTexture.colorSpace = THREE.SRGBColorSpace;
    this.sunGlow = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: glowTexture,
        color: 0xffd17c,
        transparent: true,
        opacity: 0.88,
        blending: THREE.AdditiveBlending,
        depthTest: false,
        depthWrite: false,
        toneMapped: false
      })
    );
    this.sunGlow.scale.set(21, 21, 1);
    this.sunGlow.renderOrder = -4;
    this.sunGroup.add(this.sunGlow);

    const sun = new THREE.Mesh(
      new THREE.SphereGeometry(4.35, 24, 18),
      new THREE.MeshBasicMaterial({
        color: 0xffdf78,
        depthTest: false,
        depthWrite: false,
        toneMapped: false
      })
    );
    sun.renderOrder = -3;
    this.sunGroup.add(sun);
    this.scene.add(this.sunGroup);

    const starGeometry = new THREE.BufferGeometry();
    const starPositions = [];
    for (let index = 0; index < 180; index += 1) {
      const angle = seededRandom(index + 1) * TAU;
      const radius = 55 + seededRandom(index + 11) * 52;
      const y = 20 + seededRandom(index + 21) * 46;
      starPositions.push(Math.cos(angle) * radius, y, Math.sin(angle) * radius);
    }
    starGeometry.setAttribute(
      "position",
      new THREE.Float32BufferAttribute(starPositions, 3)
    );
    const stars = new THREE.Points(
      starGeometry,
      new THREE.PointsMaterial({
        color: 0xb7e9ff,
        size: 0.5,
        transparent: true,
        opacity: 0.72,
        sizeAttenuation: true
      })
    );
    this.scene.add(stars);

    const cloudMaterial = new THREE.MeshBasicMaterial({
      color: 0xe7f8f4,
      transparent: true,
      opacity: 0.68,
      depthWrite: false
    });
    const cloudShadowMaterial = new THREE.MeshBasicMaterial({
      color: 0x7fb4c6,
      transparent: true,
      opacity: 0.16,
      depthWrite: false
    });
    const cloudGeometry = new THREE.SphereGeometry(1, 14, 9);
    const cloudAnchors = [
      [-38, 34, -30],
      [-12, 39, -49],
      [18, 35, -43],
      [37, 30, -25],
      [-25, 28, -18],
      [4, 32, -30]
    ];
    const cloudPuffs = [
      [-4.1, 0, 0, 2.5, 1.28, 1.32],
      [-2.25, 0.72, 0, 2.9, 1.72, 1.5],
      [0, 1.05, 0, 3.25, 2.05, 1.62],
      [2.3, 0.68, 0, 2.9, 1.68, 1.48],
      [4.2, 0.02, 0, 2.42, 1.22, 1.3],
      [0.35, 0.2, 0.85, 3.55, 1.42, 1.86]
    ];
    for (
      let cloudIndex = 0;
      cloudIndex < cloudAnchors.length;
      cloudIndex += 1
    ) {
      const cloud = new THREE.Group();
      for (let puffIndex = 0; puffIndex < cloudPuffs.length; puffIndex += 1) {
        const puff = new THREE.Mesh(cloudGeometry, cloudMaterial);
        const [x, y, z, scaleX, scaleY, scaleZ] = cloudPuffs[puffIndex];
        puff.position.set(x, y, z);
        puff.scale.set(scaleX, scaleY, scaleZ);
        cloud.add(puff);
      }

      const shadow = new THREE.Mesh(
        new THREE.SphereGeometry(4.8, 16, 8),
        cloudShadowMaterial
      );
      shadow.scale.set(1.35, 0.24, 0.62);
      shadow.position.y = -0.45;
      cloud.add(shadow);

      const anchor = cloudAnchors[cloudIndex];
      cloud.position.set(anchor[0], anchor[1], anchor[2]);
      cloud.scale.setScalar(0.88 + (cloudIndex % 3) * 0.12);
      cloud.userData.speed = 0.45 + cloudIndex * 0.035;
      cloud.userData.baseY = anchor[1];
      cloud.userData.phase = cloudIndex * 1.37;
      this.clouds.push(cloud);
      this.scene.add(cloud);
    }
  }

  createWater() {
    const waterGeometry = new THREE.PlaneGeometry(520, 520, 64, 64);
    const waterMaterial = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uDeep: { value: new THREE.Color(0x063f66) },
        uShallow: { value: new THREE.Color(0x0eb1bd) }
      },
      vertexShader: `
        uniform float uTime;
        varying float vWave;
        varying vec2 vUvWater;

        void main() {
          vec3 transformed = position;
          float waveA = sin(position.x * 0.13 + uTime * 0.85);
          float waveB = cos(position.y * 0.17 - uTime * 0.62);
          float ripple = sin((position.x + position.y) * 0.08 + uTime);
          transformed.z += (waveA + waveB) * 0.12 + ripple * 0.05;
          vWave = (waveA + waveB + 2.0) * 0.25;
          vUvWater = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(transformed, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec3 uDeep;
        uniform vec3 uShallow;
        uniform float uTime;
        varying float vWave;
        varying vec2 vUvWater;

        void main() {
          float band = sin((vUvWater.x - vUvWater.y) * 44.0 + uTime * 1.6);
          float shimmer = smoothstep(0.74, 1.0, band * 0.5 + 0.5) * 0.13;
          vec2 centered = vUvWater - 0.5;
          float sunTrail = exp(-abs(centered.x + centered.y * 0.18) * 28.0);
          sunTrail *= smoothstep(-0.18, 0.38, centered.y);
          float glitterA = sin(centered.y * 116.0 - uTime * 2.3);
          float glitterB = sin(centered.x * 154.0 + uTime * 1.7);
          float glitter = pow(max(0.0, glitterA * glitterB), 9.0);
          vec3 color = mix(uDeep, uShallow, 0.36 + vWave * 0.42);
          color += vec3(0.32, 0.7, 0.76) * shimmer;
          color += vec3(1.0, 0.72, 0.34) * sunTrail * (0.035 + glitter * 0.32);
          gl_FragColor = vec4(color, 1.0);
        }
      `
    });
    const water = new THREE.Mesh(waterGeometry, waterMaterial);
    water.rotation.x = -Math.PI / 2;
    water.position.y = -2.08;
    this.waterMaterial = waterMaterial;
    this.scene.add(water);

    const foamSettings = [
      {
        inner: 0.755,
        outer: 0.81,
        opacity: 0.23,
        color: 0xbffcff,
        phase: 0
      },
      {
        inner: 0.82,
        outer: 0.836,
        opacity: 0.14,
        color: 0x88edf2,
        phase: Math.PI * 0.7
      }
    ];
    foamSettings.forEach(setting => {
      const foam = new THREE.Mesh(
        new THREE.RingGeometry(setting.inner, setting.outer, 128),
        new THREE.MeshBasicMaterial({
          color: setting.color,
          transparent: true,
          opacity: setting.opacity,
          side: THREE.DoubleSide,
          depthWrite: false
        })
      );
      foam.rotation.x = -Math.PI / 2;
      foam.position.y = -1.91;
      foam.scale.set(34.5, 31.5, 1);
      foam.userData.baseOpacity = setting.opacity;
      foam.userData.phase = setting.phase;
      foam.renderOrder = 2;
      this.shoreRings.push(foam);
      this.scene.add(foam);
    });

    const ringMaterial = new THREE.MeshBasicMaterial({
      color: 0x85eff5,
      transparent: true,
      opacity: 0.14,
      side: THREE.DoubleSide,
      depthWrite: false
    });
    for (let index = 0; index < 10; index += 1) {
      const ring = new THREE.Mesh(
        new THREE.RingGeometry(1.2 + (index % 3), 1.28 + (index % 3), 40),
        ringMaterial
      );
      const angle = seededRandom(index + 90) * TAU;
      const radius = 37 + seededRandom(index + 120) * 24;
      ring.position.set(
        Math.cos(angle) * radius,
        -1.92,
        Math.sin(angle) * radius
      );
      ring.rotation.x = -Math.PI / 2;
      ring.scale.set(1.4, 1.4, 1.4);
      this.scene.add(ring);
    }
  }

  createTerrain() {
    const geometry = new THREE.PlaneGeometry(70, 70, 72, 72);
    geometry.rotateX(-Math.PI / 2);
    const position = geometry.getAttribute("position");
    const colors = [];
    const color = new THREE.Color();

    for (let index = 0; index < position.count; index += 1) {
      const x = position.getX(index);
      const z = position.getZ(index);
      const y = this.heightAt(x, z);
      position.setY(index, y);

      if (y < -1.7) color.set(0x8f6c4a);
      else if (y < -0.45) color.set(0xd2a85d);
      else if (y < 1.4) color.set(0x2b8a5f);
      else if (y < 4.4) color.set(0x176c57);
      else color.set(0x3d7562);

      const tint = (Math.sin(x * 0.7 + z * 0.42) + 1) * 0.035;
      colors.push(color.r + tint, color.g + tint, color.b + tint * 0.6);
    }

    geometry.setAttribute("color", new THREE.Float32BufferAttribute(colors, 3));
    geometry.computeVertexNormals();

    const material = new THREE.MeshStandardMaterial({
      vertexColors: true,
      flatShading: true,
      roughness: 0.94,
      metalness: 0.02,
      side: THREE.DoubleSide
    });
    this.terrain = new THREE.Mesh(geometry, material);
    this.terrain.userData.kind = "terrain";
    this.raycastTargets.push(this.terrain);
    this.scene.add(this.terrain);
  }

  createPaths() {
    const outerLocations = this.locations.filter(
      location => location.id !== "play"
    );
    const stepsPerPath = 11;
    const geometry = new THREE.CylinderGeometry(0.62, 0.68, 0.16, 8);
    const material = new THREE.MeshStandardMaterial({
      color: 0xe3bb72,
      roughness: 0.92,
      metalness: 0.04
    });
    const pathMesh = new THREE.InstancedMesh(
      geometry,
      material,
      Math.max(1, outerLocations.length * stepsPerPath)
    );
    const matrix = new THREE.Matrix4();
    const quaternion = new THREE.Quaternion();
    const scale = new THREE.Vector3();
    const position = new THREE.Vector3();
    let instance = 0;

    outerLocations.forEach((location, locationIndex) => {
      for (let step = 1; step <= stepsPerPath; step += 1) {
        const progress = step / (stepsPerPath + 1);
        const bend =
          Math.sin(progress * Math.PI) * (locationIndex % 2 ? 1.2 : -1.2);
        const x = location.worldX * progress + bend * (location.worldZ / 28);
        const z = location.worldZ * progress - bend * (location.worldX / 28);
        position.set(x, this.heightAt(x, z) + 0.12, z);
        scale.setScalar(0.76 + (step % 3) * 0.06);
        matrix.compose(position, quaternion, scale);
        pathMesh.setMatrixAt(instance, matrix);
        instance += 1;
      }
    });
    pathMesh.count = instance;
    pathMesh.instanceMatrix.needsUpdate = true;
    this.scene.add(pathMesh);
  }

  createDecorations() {
    const trunkGeometry = new THREE.CylinderGeometry(0.12, 0.18, 0.9, 6);
    const trunkMaterial = new THREE.MeshStandardMaterial({
      color: 0x5b3827,
      roughness: 1
    });
    const crownGeometry = new THREE.ConeGeometry(0.72, 1.9, 7);
    const crownMaterial = new THREE.MeshStandardMaterial({
      color: 0x0d8267,
      roughness: 0.9
    });
    const treeCount = 58;
    const trunks = new THREE.InstancedMesh(
      trunkGeometry,
      trunkMaterial,
      treeCount
    );
    const crowns = new THREE.InstancedMesh(
      crownGeometry,
      crownMaterial,
      treeCount
    );
    const matrix = new THREE.Matrix4();
    const quaternion = new THREE.Quaternion();
    const scale = new THREE.Vector3();
    const position = new THREE.Vector3();
    let placed = 0;

    for (let index = 0; index < 260 && placed < treeCount; index += 1) {
      const angle = seededRandom(index + 200) * TAU;
      const radius = 10 + seededRandom(index + 300) * 21;
      const x = Math.cos(angle) * radius * 1.06;
      const z = Math.sin(angle) * radius * 0.92;
      const y = this.heightAt(x, z);
      const tooClose = this.locations.some(location => {
        const dx = location.worldX - x;
        const dz = location.worldZ - z;
        return dx * dx + dz * dz < 26;
      });
      if (tooClose || y < -0.35) continue;

      const treeScale = 0.72 + seededRandom(index + 400) * 0.62;
      position.set(x, y + 0.45 * treeScale, z);
      scale.set(treeScale, treeScale, treeScale);
      matrix.compose(position, quaternion, scale);
      trunks.setMatrixAt(placed, matrix);

      position.y = y + 1.62 * treeScale;
      matrix.compose(position, quaternion, scale);
      crowns.setMatrixAt(placed, matrix);
      placed += 1;
    }

    trunks.count = placed;
    crowns.count = placed;
    trunks.instanceMatrix.needsUpdate = true;
    crowns.instanceMatrix.needsUpdate = true;
    this.scene.add(trunks, crowns);

    const crystalGeometry = new THREE.OctahedronGeometry(0.55, 0);
    const crystalMaterial = new THREE.MeshStandardMaterial({
      color: 0x52ebd1,
      emissive: 0x087f91,
      emissiveIntensity: 1.2,
      roughness: 0.26,
      metalness: 0.25
    });
    for (let index = 0; index < 18; index += 1) {
      const angle = seededRandom(index + 500) * TAU;
      const radius = 8 + seededRandom(index + 510) * 23;
      const x = Math.cos(angle) * radius;
      const z = Math.sin(angle) * radius * 0.91;
      const y = this.heightAt(x, z);
      if (y < -0.25) continue;
      const crystal = new THREE.Mesh(crystalGeometry, crystalMaterial);
      crystal.position.set(x, y + 0.55, z);
      crystal.scale.y = 1.35 + seededRandom(index + 520);
      crystal.rotation.y = seededRandom(index + 530) * TAU;
      this.scene.add(crystal);
    }

    const motePositions = [];
    for (let index = 0; index < 42; index += 1) {
      const angle = seededRandom(index + 610) * TAU;
      const radius = 8 + seededRandom(index + 620) * 29;
      motePositions.push(
        Math.cos(angle) * radius,
        4.5 + seededRandom(index + 630) * 14,
        Math.sin(angle) * radius * 0.92
      );
    }
    const moteGeometry = new THREE.BufferGeometry();
    moteGeometry.setAttribute(
      "position",
      new THREE.Float32BufferAttribute(motePositions, 3)
    );
    this.airMotes = new THREE.Points(
      moteGeometry,
      new THREE.PointsMaterial({
        color: 0xffe29a,
        size: 0.34,
        transparent: true,
        opacity: 0.54,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        sizeAttenuation: true
      })
    );
    this.scene.add(this.airMotes);
  }

  createLandmarks() {
    this.locations.forEach(location => {
      const group = new THREE.Group();
      const groundY = this.heightAt(location.worldX, location.worldZ);
      group.position.set(location.worldX, groundY + 0.18, location.worldZ);
      group.userData.locationId = location.id;
      group.userData.baseY = group.position.y;

      this.addLandmarkPlatform(group, location);

      if (location.id === "deposit") this.buildDeposit(group, location);
      else if (location.id === "withdraw") this.buildWithdraw(group, location);
      else if (location.id === "spin") this.buildSpin(group, location);
      else if (location.id === "roulette") this.buildRoulette(group, location);
      else if (location.id === "reward") this.buildReward(group, location);
      else if (location.id === "friend") this.buildFriend(group, location);
      else this.buildPlay(group, location);

      const label = this.createLabelSprite(location);
      label.position.set(0, location.id === "play" ? 8.6 : 7.15, 0);
      group.add(label);

      group.traverse(child => {
        child.userData.locationId = location.id;
      });

      this.locationGroups.set(location.id, group);
      this.raycastTargets.push(group);
      this.scene.add(group);
    });
  }

  addLandmarkPlatform(group, location) {
    const accent = new THREE.Color(location.accent);
    const contactShadow = new THREE.Mesh(
      new THREE.CircleGeometry(location.id === "play" ? 4.9 : 4.05, 32),
      new THREE.MeshBasicMaterial({
        color: 0x02111b,
        transparent: true,
        opacity: location.id === "play" ? 0.2 : 0.15,
        depthWrite: false
      })
    );
    contactShadow.rotation.x = -Math.PI / 2;
    contactShadow.position.y = 0.035;
    contactShadow.scale.y = 0.68;
    group.add(contactShadow);

    const platform = new THREE.Mesh(
      new THREE.CylinderGeometry(
        location.id === "play" ? 3.7 : 3,
        location.id === "play" ? 4.05 : 3.35,
        0.62,
        10
      ),
      new THREE.MeshStandardMaterial({
        color: accent.clone().multiplyScalar(0.38),
        emissive: accent,
        emissiveIntensity: 0.16,
        roughness: 0.7,
        metalness: 0.18
      })
    );
    platform.position.y = 0.31;
    group.add(platform);

    const ring = new THREE.Mesh(
      new THREE.RingGeometry(
        location.id === "play" ? 3.88 : 3.16,
        location.id === "play" ? 4.16 : 3.42,
        48
      ),
      new THREE.MeshBasicMaterial({
        color: accent,
        transparent: true,
        opacity: 0.48,
        side: THREE.DoubleSide,
        depthWrite: false
      })
    );
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = 0.66;
    ring.userData.selectionRing = true;
    group.add(ring);
  }

  makeBuildingMaterial(color, emissiveIntensity = 0.14) {
    return new THREE.MeshStandardMaterial({
      color: new THREE.Color(color).multiplyScalar(0.7),
      emissive: new THREE.Color(color),
      emissiveIntensity,
      roughness: 0.63,
      metalness: 0.24
    });
  }

  addArrow(group, direction, color, position) {
    const arrow = new THREE.Group();
    const material = new THREE.MeshStandardMaterial({
      color,
      emissive: color,
      emissiveIntensity: 0.72,
      roughness: 0.34,
      metalness: 0.12
    });
    const stem = new THREE.Mesh(
      new THREE.BoxGeometry(0.52, 1.34, 0.34),
      material
    );
    const point = new THREE.Mesh(
      new THREE.ConeGeometry(0.74, 0.9, 4),
      material
    );
    stem.position.y = direction === "down" ? 0.42 : -0.42;
    point.position.y = direction === "down" ? -0.7 : 0.7;
    if (direction === "down") point.rotation.z = Math.PI;
    arrow.add(stem, point);
    arrow.position.copy(position);
    group.add(arrow);
  }

  buildDeposit(group, location) {
    const material = this.makeBuildingMaterial(location.accent);
    const body = new THREE.Mesh(new THREE.BoxGeometry(4.3, 2.7, 3.5), material);
    body.position.y = 2.02;
    group.add(body);

    const roof = new THREE.Mesh(
      new THREE.ConeGeometry(3.25, 1.6, 4),
      new THREE.MeshStandardMaterial({
        color: 0xe0b95d,
        roughness: 0.55,
        metalness: 0.28
      })
    );
    roof.position.y = 4.18;
    roof.rotation.y = Math.PI / 4;
    group.add(roof);

    const coinMaterial = new THREE.MeshStandardMaterial({
      color: 0xffcf55,
      emissive: 0x8a4b08,
      emissiveIntensity: 0.34,
      metalness: 0.62,
      roughness: 0.28
    });
    for (let index = 0; index < 4; index += 1) {
      const coin = new THREE.Mesh(
        new THREE.CylinderGeometry(0.48, 0.48, 0.18, 18),
        coinMaterial
      );
      coin.rotation.x = Math.PI / 2;
      coin.position.set(-1.35 + index * 0.9, 4.25 + index * 0.38, 0.45);
      group.add(coin);
    }
    this.addArrow(
      group,
      "down",
      new THREE.Color(location.accent),
      new THREE.Vector3(0, 2.35, 1.94)
    );
  }

  buildWithdraw(group, location) {
    const material = this.makeBuildingMaterial(location.accent);
    const body = new THREE.Mesh(new THREE.BoxGeometry(4.6, 3.2, 3.6), material);
    body.position.y = 2.2;
    group.add(body);

    const cap = new THREE.Mesh(
      new THREE.CylinderGeometry(2.25, 2.45, 1.2, 12),
      material
    );
    cap.position.y = 4.35;
    group.add(cap);

    const doorMaterial = new THREE.MeshStandardMaterial({
      color: 0x6fcfff,
      emissive: 0x175eaa,
      emissiveIntensity: 0.5,
      metalness: 0.66,
      roughness: 0.26
    });
    const door = new THREE.Mesh(
      new THREE.CylinderGeometry(1.25, 1.25, 0.38, 24),
      doorMaterial
    );
    door.rotation.x = Math.PI / 2;
    door.position.set(0, 2.35, 2.02);
    group.add(door);

    for (let index = 0; index < 6; index += 1) {
      const bolt = new THREE.Mesh(
        new THREE.SphereGeometry(0.12, 8, 6),
        new THREE.MeshBasicMaterial({ color: 0xe8f9ff })
      );
      const angle = (index / 6) * TAU;
      bolt.position.set(
        Math.cos(angle) * 0.87,
        2.35 + Math.sin(angle) * 0.87,
        2.24
      );
      group.add(bolt);
    }
    this.addArrow(
      group,
      "up",
      new THREE.Color(location.accent),
      new THREE.Vector3(0, 2.35, 2.45)
    );
  }

  buildSpin(group, location) {
    const material = this.makeBuildingMaterial(location.accent);
    const towerGeometry = new THREE.CylinderGeometry(0.55, 0.72, 4, 8);
    [-1.9, 1.9].forEach(x => {
      const tower = new THREE.Mesh(towerGeometry, material);
      tower.position.set(x, 2.65, 0);
      group.add(tower);
    });

    const wheelGroup = new THREE.Group();
    wheelGroup.position.set(0, 3.45, 0.42);
    const rim = new THREE.Mesh(
      new THREE.TorusGeometry(1.75, 0.24, 10, 36),
      new THREE.MeshStandardMaterial({
        color: 0xffcd62,
        emissive: 0x8c2f55,
        emissiveIntensity: 0.42,
        metalness: 0.46,
        roughness: 0.32
      })
    );
    wheelGroup.add(rim);

    const hub = new THREE.Mesh(
      new THREE.CylinderGeometry(0.48, 0.48, 0.48, 18),
      material
    );
    hub.rotation.x = Math.PI / 2;
    wheelGroup.add(hub);

    for (let index = 0; index < 8; index += 1) {
      const spoke = new THREE.Mesh(
        new THREE.BoxGeometry(0.12, 1.52, 0.12),
        new THREE.MeshBasicMaterial({
          color: index % 2 ? 0xff5f91 : 0x8a52d8
        })
      );
      spoke.position.y = 0.78;
      spoke.rotation.z = (index / 8) * TAU;
      wheelGroup.add(spoke);
    }
    group.add(wheelGroup);
    this.spinWheel = wheelGroup;

    const pointer = new THREE.Mesh(
      new THREE.ConeGeometry(0.38, 0.7, 3),
      new THREE.MeshBasicMaterial({ color: 0xffe491 })
    );
    pointer.position.set(0, 5.75, 0.52);
    pointer.rotation.z = Math.PI;
    group.add(pointer);
  }

  buildRoulette(group, location) {
    const material = this.makeBuildingMaterial(location.accent);
    const base = new THREE.Mesh(
      new THREE.CylinderGeometry(2.45, 2.8, 1.6, 20),
      material
    );
    base.position.y = 1.35;
    group.add(base);

    const wheel = new THREE.Mesh(
      new THREE.CylinderGeometry(2.04, 2.04, 0.34, 28),
      new THREE.MeshStandardMaterial({
        color: 0x732f2f,
        emissive: 0xff5a46,
        emissiveIntensity: 0.28,
        metalness: 0.4,
        roughness: 0.34
      })
    );
    wheel.position.y = 2.36;
    group.add(wheel);

    const hub = new THREE.Mesh(
      new THREE.CylinderGeometry(0.52, 0.72, 0.88, 16),
      new THREE.MeshStandardMaterial({
        color: 0xffcb66,
        metalness: 0.62,
        roughness: 0.24
      })
    );
    hub.position.y = 2.86;
    group.add(hub);

    const ball = new THREE.Mesh(
      new THREE.SphereGeometry(0.25, 12, 8),
      new THREE.MeshStandardMaterial({
        color: 0xffffff,
        emissive: 0x5fdfff,
        emissiveIntensity: 0.55,
        roughness: 0.18
      })
    );
    ball.position.set(1.55, 2.78, 0);
    group.add(ball);
    this.rouletteBall = ball;

    for (let index = 0; index < 12; index += 1) {
      const marker = new THREE.Mesh(
        new THREE.BoxGeometry(0.18, 0.09, 0.72),
        new THREE.MeshBasicMaterial({
          color: index % 2 ? 0xffd36a : 0x20132d
        })
      );
      marker.position.y = 2.58;
      marker.rotation.y = (index / 12) * TAU;
      marker.position.x = Math.cos((index / 12) * TAU) * 1.45;
      marker.position.z = Math.sin((index / 12) * TAU) * 1.45;
      group.add(marker);
    }
  }

  buildReward(group, location) {
    const material = this.makeBuildingMaterial(location.accent);
    const gift = new THREE.Mesh(new THREE.BoxGeometry(4.4, 3.5, 4.1), material);
    gift.position.y = 2.42;
    group.add(gift);

    const ribbonMaterial = new THREE.MeshStandardMaterial({
      color: 0xffd967,
      emissive: 0x8f5b0c,
      emissiveIntensity: 0.25,
      metalness: 0.48,
      roughness: 0.3
    });
    const verticalRibbon = new THREE.Mesh(
      new THREE.BoxGeometry(0.72, 3.65, 4.26),
      ribbonMaterial
    );
    verticalRibbon.position.y = 2.48;
    group.add(verticalRibbon);
    const crossRibbon = new THREE.Mesh(
      new THREE.BoxGeometry(4.55, 0.7, 4.26),
      ribbonMaterial
    );
    crossRibbon.position.y = 3.15;
    group.add(crossRibbon);

    [-0.72, 0.72].forEach((x, index) => {
      const bow = new THREE.Mesh(
        new THREE.TorusGeometry(0.72, 0.22, 8, 20),
        ribbonMaterial
      );
      bow.position.set(x, 4.65, 0.1);
      bow.rotation.y = index ? -0.45 : 0.45;
      bow.scale.x = 1.22;
      group.add(bow);
    });
  }

  buildFriend(group, location) {
    const material = this.makeBuildingMaterial(location.accent);
    const skinMaterial = new THREE.MeshStandardMaterial({
      color: 0xffd8bd,
      roughness: 0.75
    });
    [-1.15, 1.15].forEach((x, index) => {
      const body = new THREE.Mesh(
        new THREE.CylinderGeometry(0.65, 0.88, 2.3, 10),
        material
      );
      body.position.set(x, 2.05, 0);
      group.add(body);

      const head = new THREE.Mesh(
        new THREE.SphereGeometry(0.68, 14, 10),
        skinMaterial
      );
      head.position.set(x, 3.85, 0);
      group.add(head);

      const arm = new THREE.Mesh(
        new THREE.CylinderGeometry(0.2, 0.2, 2.1, 8),
        skinMaterial
      );
      arm.position.set(x * 0.48, 2.55, 0.4);
      arm.rotation.z = index ? -0.92 : 0.92;
      group.add(arm);
    });

    const bond = new THREE.Mesh(
      new THREE.TorusGeometry(1.7, 0.17, 8, 28, Math.PI),
      new THREE.MeshStandardMaterial({
        color: 0xf5dcff,
        emissive: new THREE.Color(location.accent),
        emissiveIntensity: 0.68,
        transparent: true,
        opacity: 0.9
      })
    );
    bond.position.y = 5.3;
    group.add(bond);
  }

  buildPlay(group, location) {
    const material = this.makeBuildingMaterial(location.accent, 0.24);
    [-2.35, 2.35].forEach(x => {
      const tower = new THREE.Mesh(
        new THREE.CylinderGeometry(0.72, 0.96, 4.6, 10),
        material
      );
      tower.position.set(x, 2.9, 0);
      group.add(tower);
    });

    const arch = new THREE.Mesh(
      new THREE.TorusGeometry(2.36, 0.58, 12, 36, Math.PI),
      new THREE.MeshStandardMaterial({
        color: 0xffb565,
        emissive: 0xff7029,
        emissiveIntensity: 0.46,
        metalness: 0.38,
        roughness: 0.35
      })
    );
    arch.position.y = 4.8;
    group.add(arch);

    const portalMaterial = new THREE.MeshBasicMaterial({
      color: 0x4effe6,
      transparent: true,
      opacity: 0.78,
      side: THREE.DoubleSide,
      depthWrite: false
    });
    const portal = new THREE.Mesh(
      new THREE.CircleGeometry(2.05, 36, 0, Math.PI),
      portalMaterial
    );
    portal.position.set(0, 4.72, 0.08);
    group.add(portal);
    this.portalCore = portal;

    const controller = new THREE.Group();
    const controllerMaterial = new THREE.MeshStandardMaterial({
      color: 0x172747,
      emissive: 0x2fe4e2,
      emissiveIntensity: 0.34,
      roughness: 0.42
    });
    const controllerBody = new THREE.Mesh(
      new THREE.BoxGeometry(3.2, 1.45, 0.7),
      controllerMaterial
    );
    controllerBody.position.y = 4.25;
    controller.add(controllerBody);
    [-1.22, 1.22].forEach(x => {
      const grip = new THREE.Mesh(
        new THREE.SphereGeometry(0.75, 14, 10),
        controllerMaterial
      );
      grip.scale.set(0.85, 1.25, 0.65);
      grip.position.set(x, 3.72, 0);
      controller.add(grip);
    });
    const buttonMaterial = new THREE.MeshBasicMaterial({ color: 0xffb45b });
    [-0.26, 0.26].forEach((x, index) => {
      const button = new THREE.Mesh(
        new THREE.SphereGeometry(0.16, 10, 7),
        buttonMaterial
      );
      button.position.set(0.75 + x, 4.36 + index * 0.15, 0.4);
      controller.add(button);
    });
    group.add(controller);
  }

  createLabelSprite(location) {
    const canvas = document.createElement("canvas");
    canvas.width = 512;
    canvas.height = 152;
    const context = canvas.getContext("2d");
    const accent = location.accent;

    context.fillStyle = "rgba(5, 20, 38, 0.9)";
    context.strokeStyle = accent;
    context.lineWidth = 8;
    const left = 8;
    const top = 8;
    const width = 496;
    const height = 136;
    const radius = 38;
    context.beginPath();
    context.moveTo(left + radius, top);
    context.lineTo(left + width - radius, top);
    context.quadraticCurveTo(left + width, top, left + width, top + radius);
    context.lineTo(left + width, top + height - radius);
    context.quadraticCurveTo(
      left + width,
      top + height,
      left + width - radius,
      top + height
    );
    context.lineTo(left + radius, top + height);
    context.quadraticCurveTo(left, top + height, left, top + height - radius);
    context.lineTo(left, top + radius);
    context.quadraticCurveTo(left, top, left + radius, top);
    context.closePath();
    context.fill();
    context.stroke();

    context.beginPath();
    context.fillStyle = accent;
    context.arc(66, 76, 38, 0, TAU);
    context.fill();

    context.fillStyle = "#061527";
    context.font = "700 42px Tahoma, Arial, sans-serif";
    context.textAlign = "center";
    context.textBaseline = "middle";
    context.fillText(location.glyph, 66, 77);

    context.textAlign = "left";
    context.fillStyle = "#ffffff";
    context.font = "700 38px Tahoma, Arial, sans-serif";
    context.fillText(location.label, 122, 62);
    context.fillStyle = "#9fd5df";
    context.font = "500 23px Tahoma, Arial, sans-serif";
    context.fillText(location.shortHint, 122, 103);

    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.minFilter = THREE.LinearFilter;
    const material = new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
      depthTest: false,
      depthWrite: false
    });
    const sprite = new THREE.Sprite(material);
    sprite.userData.labelSprite = true;
    sprite.userData.baseWidth = 7.35;
    sprite.userData.baseHeight = 2.18;
    sprite.scale.set(7.35, 2.18, 1);
    sprite.renderOrder = 30;
    return sprite;
  }

  createRaycastHelpers() {
    const hoverMaterial = new THREE.MeshBasicMaterial({
      color: 0x7ff7ee,
      transparent: true,
      opacity: 0.84,
      side: THREE.DoubleSide,
      depthWrite: false
    });
    this.hoverMarker = new THREE.Mesh(
      new THREE.RingGeometry(0.48, 0.72, 32),
      hoverMaterial
    );
    this.hoverMarker.rotation.x = -Math.PI / 2;
    this.hoverMarker.visible = false;
    this.scene.add(this.hoverMarker);

    const pickMaterial = new THREE.MeshBasicMaterial({
      color: 0xffcc70,
      transparent: true,
      opacity: 0.88,
      side: THREE.DoubleSide,
      depthWrite: false
    });
    this.pickMarker = new THREE.Group();
    const pickRing = new THREE.Mesh(
      new THREE.RingGeometry(0.8, 1.08, 40),
      pickMaterial
    );
    pickRing.rotation.x = -Math.PI / 2;
    this.pickMarker.add(pickRing);

    const beam = new THREE.Mesh(
      new THREE.CylinderGeometry(0.035, 0.12, 3.8, 8),
      pickMaterial
    );
    beam.position.y = 1.9;
    this.pickMarker.add(beam);
    this.pickMarker.visible = false;
    this.scene.add(this.pickMarker);
  }

  resolveLocationId(object) {
    let current = object;
    while (current) {
      if (current.userData && current.userData.locationId) {
        return current.userData.locationId;
      }
      current = current.parent;
    }
    return null;
  }

  updatePointerNdc(clientX, clientY, rect) {
    this.pointerNdc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
    this.pointerNdc.y = -((clientY - rect.top) / rect.height) * 2 + 1;
  }

  performRaycast(clientX, clientY, rect, options = {}) {
    if (!rect.width || !rect.height) return null;
    this.updatePointerNdc(clientX, clientY, rect);
    this.raycaster.setFromCamera(this.pointerNdc, this.camera);
    const intersections = this.raycaster.intersectObjects(
      this.raycastTargets,
      true
    );
    this.raycastCount += 1;

    let terrainIntersection = null;
    let locationIntersection = null;
    let locationId = null;

    for (let index = 0; index < intersections.length; index += 1) {
      const intersection = intersections[index];
      const hitLocationId = this.resolveLocationId(intersection.object);
      if (hitLocationId && !locationIntersection) {
        locationIntersection = intersection;
        locationId = hitLocationId;
        break;
      }
      if (
        !terrainIntersection &&
        intersection.object.userData &&
        intersection.object.userData.kind === "terrain"
      ) {
        terrainIntersection = intersection;
      }
    }

    if (options.hover) {
      this.hoveredId = locationId;
      if (locationIntersection) {
        this.hoverMarker.visible = false;
        this.canvas.style.cursor = "pointer";
      } else if (terrainIntersection) {
        this.hoverMarker.position.copy(terrainIntersection.point);
        this.hoverMarker.position.y += 0.12;
        this.hoverMarker.visible = true;
        this.canvas.style.cursor = "crosshair";
      } else {
        this.hoverMarker.visible = false;
        this.canvas.style.cursor = "grab";
      }
      this.updateSelectionVisuals();
      return locationIntersection || terrainIntersection;
    }

    if (locationIntersection && locationId) {
      this.setSelected(locationId);
      this.onSelect(locationId, {
        x: locationIntersection.point.x,
        y: locationIntersection.point.y,
        z: locationIntersection.point.z
      });
      return locationIntersection;
    }

    if (terrainIntersection) {
      this.terrainPick = {
        x: terrainIntersection.point.x,
        y: terrainIntersection.point.y,
        z: terrainIntersection.point.z
      };
      this.pickMarker.position.copy(terrainIntersection.point);
      this.pickMarker.position.y += 0.1;
      this.pickMarker.visible = true;
      this.onTerrainPick({ ...this.terrainPick });
      return terrainIntersection;
    }
    return null;
  }

  handlePointerDown(clientX, clientY, rect, pointerId) {
    this.pointerMap.set(pointerId, {
      startX: clientX,
      startY: clientY,
      x: clientX,
      y: clientY,
      lastX: clientX,
      lastY: clientY,
      rect
    });
    this.dragging = false;
    this.canvas.style.cursor = "grabbing";

    if (this.pointerMap.size === 2) {
      const points = Array.from(this.pointerMap.values());
      this.pinchStartDistance = Math.hypot(
        points[0].x - points[1].x,
        points[0].y - points[1].y
      );
      this.pinchBaseCameraDistance = this.desiredDistance;
    }
  }

  handlePointerMove(clientX, clientY, rect, pointerId) {
    const pointer = this.pointerMap.get(pointerId);
    if (!pointer) {
      this.performRaycast(clientX, clientY, rect, { hover: true });
      return;
    }

    pointer.lastX = pointer.x;
    pointer.lastY = pointer.y;
    pointer.x = clientX;
    pointer.y = clientY;
    pointer.rect = rect;

    if (this.pointerMap.size >= 2) {
      const points = Array.from(this.pointerMap.values()).slice(0, 2);
      const distance = Math.max(
        20,
        Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y)
      );
      if (!this.pinchStartDistance) {
        this.pinchStartDistance = distance;
        this.pinchBaseCameraDistance = this.desiredDistance;
      }
      this.desiredDistance = clamp(
        this.pinchBaseCameraDistance * (this.pinchStartDistance / distance),
        this.minDistance,
        this.maxDistance
      );
      this.dragging = true;
      this.emitCameraState();
      return;
    }

    const totalDistance = Math.hypot(
      pointer.x - pointer.startX,
      pointer.y - pointer.startY
    );
    if (totalDistance > 5) this.dragging = true;
    if (!this.dragging) return;

    const deltaX = pointer.x - pointer.lastX;
    const deltaY = pointer.y - pointer.lastY;
    this.desiredYaw -= deltaX * 0.0075;
    this.desiredPitch = clamp(this.desiredPitch + deltaY * 0.0045, 0.98, 1.3);
    this.emitCameraState();
  }

  handlePointerUp(clientX, clientY, rect, pointerId) {
    const pointer = this.pointerMap.get(pointerId);
    const shouldPick =
      pointer &&
      !this.dragging &&
      Math.hypot(clientX - pointer.startX, clientY - pointer.startY) <= 7;

    this.pointerMap.delete(pointerId);
    if (shouldPick) this.performRaycast(clientX, clientY, rect);

    if (this.pointerMap.size < 2) {
      this.pinchStartDistance = 0;
      this.pinchBaseCameraDistance = this.desiredDistance;
    }
    if (this.pointerMap.size === 0) {
      this.dragging = false;
      this.canvas.style.cursor = this.hoveredId ? "pointer" : "grab";
    }
  }

  handlePointerCancel(pointerId) {
    this.pointerMap.delete(pointerId);
    this.pinchStartDistance = 0;
    if (this.pointerMap.size === 0) {
      this.dragging = false;
      this.canvas.style.cursor = "grab";
    }
  }

  handlePointerLeave() {
    if (this.pointerMap.size === 0) {
      this.hoverMarker.visible = false;
      this.hoveredId = null;
      this.updateSelectionVisuals();
    }
  }

  handleWheel(deltaY) {
    this.desiredDistance = clamp(
      this.desiredDistance + deltaY * 0.025,
      this.minDistance,
      this.maxDistance
    );
    this.emitCameraState();
  }

  zoomBy(amount) {
    this.desiredDistance = clamp(
      this.desiredDistance + amount,
      this.minDistance,
      this.maxDistance
    );
    this.emitCameraState();
  }

  resetCamera() {
    this.desiredYaw = 0.84;
    this.desiredPitch = 1.08;
    this.desiredDistance = 98;
    this.desiredTarget.set(0, 1.4, 0);
    this.emitCameraState();
  }

  setSelected(id, options = {}) {
    const location = this.locations.find(item => item.id === id);
    if (!location) return;
    this.selectedId = id;
    const targetY = this.heightAt(location.worldX, location.worldZ) + 1.7;
    this.desiredTarget.set(
      location.worldX * 0.42,
      targetY,
      location.worldZ * 0.42
    );
    if (options.immediate) this.cameraTarget.copy(this.desiredTarget);
    this.updateSelectionVisuals();
  }

  updateSelectionVisuals() {
    this.locationGroups.forEach((group, id) => {
      const selected = id === this.selectedId;
      const hovered = id === this.hoveredId;
      group.traverse(child => {
        if (!child.material) return;
        if (child.userData.selectionRing) {
          child.material.opacity = selected ? 0.9 : hovered ? 0.72 : 0.28;
          child.scale.setScalar(selected ? 1.08 : hovered ? 1.04 : 1);
        }
        if (child.userData.labelSprite) {
          const labelScale = selected ? 1 : hovered ? 0.9 : 0.72;
          child.material.opacity = selected ? 1 : hovered ? 0.94 : 0.78;
          child.scale.set(
            child.userData.baseWidth * labelScale,
            child.userData.baseHeight * labelScale,
            1
          );
        }
      });
    });
  }

  emitCameraState() {
    const percent = Math.round(
      ((this.maxDistance - this.desiredDistance) /
        (this.maxDistance - this.minDistance)) *
        100
    );
    this.onCameraChange({
      zoomPercent: percent,
      canZoomIn: this.desiredDistance > this.minDistance + 0.2,
      canZoomOut: this.desiredDistance < this.maxDistance - 0.2
    });
  }

  resize(width, height, devicePixelRatio = 1) {
    if (!width || !height) return;
    this.renderer.setPixelRatio(Math.min(1.5, Math.max(1, devicePixelRatio)));
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.render();
  }

  updateCamera(amount) {
    this.cameraYaw += angleDelta(this.cameraYaw, this.desiredYaw) * amount;
    this.cameraPitch = lerp(this.cameraPitch, this.desiredPitch, amount);
    this.cameraDistance = lerp(
      this.cameraDistance,
      this.desiredDistance,
      amount
    );
    this.cameraTarget.lerp(this.desiredTarget, amount);

    const horizontal = Math.cos(this.cameraPitch) * this.cameraDistance;
    this.camera.position.set(
      this.cameraTarget.x + Math.cos(this.cameraYaw) * horizontal,
      this.cameraTarget.y + Math.sin(this.cameraPitch) * this.cameraDistance,
      this.cameraTarget.z + Math.sin(this.cameraYaw) * horizontal
    );
    this.camera.lookAt(this.cameraTarget);
  }

  update(deltaSeconds) {
    this.elapsed += deltaSeconds;
    const cameraEase = 1 - Math.pow(0.001, Math.min(deltaSeconds, 0.05));
    this.updateCamera(cameraEase);

    if (this.waterMaterial) {
      this.waterMaterial.uniforms.uTime.value = this.elapsed;
    }
    if (this.spinWheel) {
      this.spinWheel.rotation.z = -this.elapsed * 0.58;
    }
    if (this.rouletteBall) {
      const angle = this.elapsed * 1.45;
      this.rouletteBall.position.x = Math.cos(angle) * 1.55;
      this.rouletteBall.position.z = Math.sin(angle) * 1.55;
      this.rouletteBall.position.y = 2.78;
    }
    if (this.portalCore) {
      const pulse = 0.72 + Math.sin(this.elapsed * 2.8) * 0.16;
      this.portalCore.material.opacity = pulse;
      this.portalCore.scale.setScalar(1 + Math.sin(this.elapsed * 2.1) * 0.035);
    }

    if (this.sunGroup) {
      this.sunGroup.position.y =
        this.sunGroup.userData.baseY + Math.sin(this.elapsed * 0.18) * 0.42;
      this.sunGroup.rotation.z = Math.sin(this.elapsed * 0.11) * 0.018;
    }
    if (this.sunGlow) {
      this.sunGlow.material.opacity =
        0.82 + Math.sin(this.elapsed * 0.72) * 0.08;
      const sunPulse = 1 + Math.sin(this.elapsed * 0.52) * 0.025;
      this.sunGlow.scale.set(21 * sunPulse, 21 * sunPulse, 1);
    }

    this.clouds.forEach((cloud, index) => {
      cloud.position.x += cloud.userData.speed * deltaSeconds;
      cloud.position.y =
        cloud.userData.baseY +
        Math.sin(this.elapsed * 0.22 + cloud.userData.phase) * 0.55;
      cloud.rotation.y =
        Math.sin(this.elapsed * 0.13 + cloud.userData.phase) * 0.035;
      if (cloud.position.x > 62) cloud.position.x = -62 - index * 3;
    });

    this.shoreRings.forEach((ring, index) => {
      const wave =
        Math.sin(this.elapsed * (0.58 + index * 0.12) + ring.userData.phase) *
          0.5 +
        0.5;
      const scalePulse = 1 + wave * (0.004 + index * 0.002);
      ring.scale.set(34.5 * scalePulse, 31.5 * scalePulse, 1);
      ring.material.opacity = ring.userData.baseOpacity * (0.68 + wave * 0.32);
    });
    if (this.airMotes) {
      this.airMotes.rotation.y = this.elapsed * 0.018;
      this.airMotes.material.opacity =
        0.46 + Math.sin(this.elapsed * 0.7) * 0.08;
    }

    this.locationGroups.forEach((group, id) => {
      const selected = id === this.selectedId;
      const baseY = group.userData.baseY;
      group.position.y =
        baseY +
        Math.sin(this.elapsed * 1.7 + group.position.x) *
          (selected ? 0.09 : 0.025);
      group.traverse(child => {
        if (child.userData.selectionRing) {
          child.rotation.z += deltaSeconds * (selected ? 0.62 : 0.18);
        }
      });
    });

    if (this.hoverMarker.visible) {
      const scale = 1 + Math.sin(this.elapsed * 5) * 0.16;
      this.hoverMarker.scale.setScalar(scale);
    }
    if (this.pickMarker.visible) {
      this.pickMarker.rotation.y += deltaSeconds * 0.9;
      const pulse = 1 + Math.sin(this.elapsed * 4) * 0.08;
      this.pickMarker.scale.setScalar(pulse);
    }
  }

  render() {
    this.renderer.render(this.scene, this.camera);
  }

  tick(timestamp) {
    if (!this.running) return;
    const deltaSeconds = this.lastFrameTime
      ? Math.min(0.05, (timestamp - this.lastFrameTime) / 1000)
      : 1 / 60;
    this.lastFrameTime = timestamp;
    this.update(deltaSeconds);
    this.render();
    this.frameHandle = requestAnimationFrame(this.tick);
  }

  start() {
    if (this.running) return;
    this.running = true;
    this.lastFrameTime = 0;
    this.emitCameraState();
    this.frameHandle = requestAnimationFrame(this.tick);
  }

  stop() {
    this.running = false;
    cancelAnimationFrame(this.frameHandle);
    this.pointerMap.clear();

    const disposedGeometries = new Set();
    const disposedMaterials = new Set();
    const disposedTextures = new Set();
    this.scene.traverse(object => {
      if (object.geometry && !disposedGeometries.has(object.geometry)) {
        disposedGeometries.add(object.geometry);
        object.geometry.dispose();
      }
      if (object.material) {
        const materials = Array.isArray(object.material)
          ? object.material
          : [object.material];
        materials.forEach(material => {
          if (!disposedMaterials.has(material)) {
            disposedMaterials.add(material);
            disposeMaterial(material, disposedTextures);
          }
        });
      }
    });
    this.renderer.dispose();
  }

  advanceTime(milliseconds) {
    const totalSeconds = Math.max(0, milliseconds) / 1000;
    const steps = Math.max(1, Math.ceil(totalSeconds / (1 / 60)));
    const deltaSeconds = steps ? totalSeconds / steps : 0;
    for (let index = 0; index < steps; index += 1) {
      this.update(deltaSeconds);
    }
    this.render();
  }

  renderGameToText() {
    return JSON.stringify({
      mode: "terrain-raycast-preview",
      coordinateSystem:
        "World origin is island center; +X east, +Z south, +Y up.",
      selectedLocation: this.selectedId,
      hoveredLocation: this.hoveredId,
      terrainPick: this.terrainPick
        ? {
            x: Number(this.terrainPick.x.toFixed(2)),
            y: Number(this.terrainPick.y.toFixed(2)),
            z: Number(this.terrainPick.z.toFixed(2))
          }
        : null,
      camera: {
        yaw: Number(this.cameraYaw.toFixed(2)),
        pitch: Number(this.cameraPitch.toFixed(2)),
        distance: Number(this.cameraDistance.toFixed(2))
      },
      raycastCount: this.raycastCount,
      controls: {
        tap: "raycast terrain or landmark",
        drag: "orbit camera",
        pinch: "zoom",
        fullscreen: "press F"
      },
      backdrop: {
        mode: "water-only",
        skyVisible: false,
        waterExtendsBeyondViewport: true
      },
      ambience: {
        shorelineFoamBands: this.shoreRings.length,
        floatingMotes: this.airMotes
          ? this.airMotes.geometry.getAttribute("position").count
          : 0,
        waterSunGlitter: Boolean(this.waterMaterial)
      }
    });
  }
}
